import { useRequesterStore } from '../stores/requester'

let requesterX: any

export type OnboardingStep = 'USAGE' | 'SETUP' | 'COMPLETED'

export type OnboardingUsage = 'PERSONAL' | 'PERSONAL_BUSINESS'

export interface OnboardingBusinessDraft {
  name: string
  billingEnabled: boolean
}

export interface OnboardingDraft {
  usage?: OnboardingUsage

  /**
   * Se utiliza cuando:
   * usage === 'PERSONAL'
   */
  billingEnabled?: boolean

  /**
   * Se utiliza cuando:
   * usage === 'PERSONAL_BUSINESS'
   */
  business?: OnboardingBusinessDraft
}

export interface UserOnboarding {
  id?: number
  userId?: string
  currentStep: OnboardingStep
  draft: OnboardingDraft | string | null
  completedAt: string | null
  createdAt?: string
  updatedAt?: string
}

/**
 * El backend permite enviar currentStep, draft o ambos.
 * Esta unión evita enviar un objeto completamente vacío.
 */
export type UpdateOnboardingPayload =
  | {
      currentStep: OnboardingStep
      draft?: OnboardingDraft
    }
  | {
      currentStep?: OnboardingStep
      draft: OnboardingDraft
    }

/**
 * Extrae el onboarding aunque la API lo devuelva:
 *
 * { onboarding: {...} }
 * { data: {...} }
 * o directamente {...}
 */
function extractOnboarding(response: any): UserOnboarding {
  const onboarding = response?.data?.onboarding ?? response?.data?.data ?? response?.data

  if (!onboarding || typeof onboarding !== 'object') {
    throw new Error('La API no devolvió el onboarding del usuario.')
  }

  return onboarding as UserOnboarding
}

/**
 * Obtiene el onboarding del usuario autenticado.
 */
export async function getCurrentOnboarding(): Promise<UserOnboarding> {
  requesterX = requesterX ?? useRequesterStore()

  const response = await requesterX.Get({
    route: '/user-onboarding/get',
    withAuth: true,
  })

  return extractOnboarding(response)
}

/**
 * Crea el onboarding del usuario autenticado.
 * Debe utilizarse al registrar usuarios nuevos si el
 * backend todavía no lo crea automáticamente.
 */
export async function createCurrentOnboarding(): Promise<UserOnboarding> {
  requesterX = requesterX ?? useRequesterStore()

  const response = await requesterX.Post({
    route: '/user-onboarding/create',
    withAuth: true,
    body: {},
  })

  return extractOnboarding(response)
}

/**
 * Actualiza currentStep, draft o ambos.
 */
export async function updateOnboarding(payload: UpdateOnboardingPayload) {
  requesterX = requesterX ?? useRequesterStore()

  return requesterX.Put({
    route: '/user-onboarding/update',
    withAuth: true,
    body: payload,
  })
}
